import React from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Autocomplete,
  Paper,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { photosAPI } from '../services/api';
import { colors } from '../theme';

const sortAlpha = (items, getKey = (item) => item) =>
  [...items].sort((a, b) =>
    String(getKey(a)).localeCompare(String(getKey(b)), undefined, { sensitivity: 'base', numeric: true }),
  );

const PhotoGalleryPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPhoto, setSelectedPhoto] = React.useState(null);
  const [showFilters, setShowFilters] = React.useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('q') || '');
  const [selectedYear, setSelectedYear] = React.useState(searchParams.get('year') || '');
  const [selectedServiceType, setSelectedServiceType] = React.useState(searchParams.get('service_type') || '');
  const [selectedClub, setSelectedClub] = React.useState(searchParams.get('club') || '');
  const [selectedBeneficiary, setSelectedBeneficiary] = React.useState(searchParams.get('beneficiary') || '');
  const [activeFiltersCount, setActiveFiltersCount] = React.useState(0);

  // Build search params
  const buildSearchParams = () => {
    const params = {};
    if (searchQuery) params.q = searchQuery;
    if (selectedYear) params.year = selectedYear;
    if (selectedServiceType) params.service_type = selectedServiceType;
    if (selectedClub) params.club = selectedClub;
    if (selectedBeneficiary) params.beneficiary = selectedBeneficiary;
    return params;
  };

  // Count active filters
  React.useEffect(() => {
    let count = 0;
    if (selectedYear) count++;
    if (selectedServiceType) count++;
    if (selectedClub) count++;
    if (selectedBeneficiary) count++;
    setActiveFiltersCount(count);
  }, [selectedYear, selectedServiceType, selectedClub, selectedBeneficiary]);

  const { data, isLoading, error } = useQuery(
    ['photos', buildSearchParams()],
    () => photosAPI.search(buildSearchParams()),
    { enabled: true }
  );

  const handleSearch = () => {
    const params = buildSearchParams();
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedYear('');
    setSelectedServiceType('');
    setSelectedClub('');
    setSelectedBeneficiary('');
    setSearchParams({});
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  // Service types for dropdown
  const serviceTypes = sortAlpha([
    { label: '青少年服務', value: '青少年服務' },
    { label: '社會服務', value: '社會服務' },
    { label: '環保服務', value: '環保服務' },
    { label: '健康服務', value: '健康服務' },
    { label: '國際服務', value: '國際服務' },
    { label: '視力服務', value: '視力服務' },
    { label: '扶貧服務', value: '扶貧服務' },
    { label: '長者服務', value: '長者服務' },
  ], (item) => item.label);

  // Years for dropdown (2000-current)
  const currentYear = new Date().getFullYear();
  const years = sortAlpha(
    Array.from({ length: currentYear - 1999 }, (_, i) => currentYear - i),
    (year) => year,
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('errorLoadingPhotos')}</Alert>
      </Container>
    );
  }

  // API returns array directly without pagination
  const photos = Array.isArray(data?.data) ? data.data : [];

  return (
    <Box sx={{ backgroundColor: colors.lightGray, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          相片庫 / Photo Gallery
        </Typography>

        {/* Search Bar */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="搜尋相片標題、標籤... / Search photos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                backgroundColor: colors.lionsGold,
                color: colors.royalBlue,
                fontWeight: 600,
                minWidth: 120,
                '&:hover': {
                  backgroundColor: '#FFCA28',
                },
              }}
            >
              {t('search')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterIcon />}
              sx={{
                minWidth: 120,
                position: 'relative',
              }}
            >
              {t('filter')}
              {activeFiltersCount > 0 && (
                <Chip
                  label={activeFiltersCount}
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    height: 20,
                    minWidth: 20,
                  }}
                />
              )}
            </Button>
          </Box>

          {/* Advanced Filters */}
          <Collapse in={showFilters}>
            <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={years}
                    value={selectedYear}
                    onChange={(e, newValue) => setSelectedYear(newValue || '')}
                    renderInput={(params) => (
                      <TextField {...params} label="出版年份 / Year" size="small" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={serviceTypes}
                    value={serviceTypes.find((s) => s.value === selectedServiceType) || null}
                    onChange={(e, newValue) => setSelectedServiceType(newValue?.value || '')}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} label="服務種類 / Service Type" size="small" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="獅子會 / Club (searchable)"
                    value={selectedClub}
                    onChange={(e) => setSelectedClub(e.target.value)}
                    placeholder="輸入獅子會名稱..."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="受惠者/協辦機構 / Beneficiary/Partner"
                    value={selectedBeneficiary}
                    onChange={(e) => setSelectedBeneficiary(e.target.value)}
                    placeholder="搜尋受惠者或協辦機構..."
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button size="small" onClick={handleSearch} variant="contained">
                  套用篩選 / Apply Filters
                </Button>
                <Button size="small" onClick={handleClearFilters} variant="outlined">
                  清除篩選 / Clear All
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {/* Results */}
        {photos.length === 0 ? (
          <Alert severity="info">{t('noResults')}</Alert>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 3 }}>
              找到 {photos.length} 張相片 / Found {photos.length} photos
            </Typography>
            <Grid container spacing={3}>
              {photos.map((photo) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => handlePhotoClick(photo)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={photo.thumbnail || photo.image}
                      alt={photo.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        {photo.title}
                      </Typography>
                      
                      {/* AI Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {photo.publication_year && (
                          <Chip label={photo.publication_year} size="small" variant="outlined" />
                        )}
                        {photo.club_name && (
                          <Chip label={photo.club_name} size="small" color="primary" variant="outlined" />
                        )}
                        {photo.service_type && (
                          <Chip label={photo.service_type} size="small" color="secondary" variant="outlined" />
                        )}
                      </Box>
                      
                      {photo.photographer && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          📷 {photo.photographer}
                        </Typography>
                      )}
                      {photo.location && (
                        <Typography variant="caption" color="text.secondary">
                          📍 {photo.location}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Photo Detail Dialog */}
        <Dialog
          open={!!selectedPhoto}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
        >
          {selectedPhoto && (
            <>
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  },
                  zIndex: 1,
                }}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent sx={{ p: 0 }}>
                <Box
                  component="img"
                  src={selectedPhoto.image}
                  alt={selectedPhoto.title}
                  sx={{ width: '100%', display: 'block' }}
                />
                <Box sx={{ p: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {selectedPhoto.title}
                  </Typography>
                  
                  {selectedPhoto.description && (
                    <Typography variant="body1" paragraph>
                      {selectedPhoto.description}
                    </Typography>
                  )}
                  
                  {/* AI Generated Tags */}
                  {(selectedPhoto.club_name || selectedPhoto.service_type || 
                    selectedPhoto.beneficiary || selectedPhoto.partner_organization || 
                    (selectedPhoto.keyword_refs || []).length > 0) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        🏷️ 標籤 / Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {selectedPhoto.publication_year && (
                          <Chip label={`📅 ${selectedPhoto.publication_year}`} />
                        )}
                        {selectedPhoto.club_name && (
                          <Chip label={`🏛️ ${selectedPhoto.club_name}`} color="primary" />
                        )}
                        {selectedPhoto.service_type && (
                          <Chip label={`🎯 ${selectedPhoto.service_type}`} color="secondary" />
                        )}
                        {selectedPhoto.beneficiary && (
                          <Chip label={`👥 ${selectedPhoto.beneficiary}`} color="info" />
                        )}
                        {selectedPhoto.partner_organization && (
                          <Chip label={`🤝 ${selectedPhoto.partner_organization}`} color="success" />
                        )}
                      </Box>
                      {(selectedPhoto.keyword_refs || []).length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          🔑 {(selectedPhoto.keyword_refs || []).map((k) => k.name).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  <Grid container spacing={2}>
                    {selectedPhoto.issue_title && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>期刊 / Issue:</strong>{' '}
                          {selectedPhoto.issue_title}
                        </Typography>
                      </Grid>
                    )}
                    {selectedPhoto.photographer && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>{t('photographer')}:</strong> {selectedPhoto.photographer}
                        </Typography>
                      </Grid>
                    )}
                    {selectedPhoto.photo_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>{t('photoDate')}:</strong> {selectedPhoto.photo_date}
                        </Typography>
                      </Grid>
                    )}
                    {selectedPhoto.location && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>{t('location')}:</strong> {selectedPhoto.location}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  
                  {/* View Page Button */}
                  {selectedPhoto.page && selectedPhoto.issue && (
                    <Box sx={{ mt: 3 }}>
                      <Button
                        component={RouterLink}
                        to={`/issues/${selectedPhoto.issue}?page=${selectedPhoto.page}`}
                        variant="contained"
                        sx={{
                          backgroundColor: colors.lionsGold,
                          color: colors.royalBlue,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#FFCA28',
                          },
                        }}
                      >
                        查看原始頁面 / View Original Page
                      </Button>
                    </Box>
                  )}
                </Box>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default PhotoGalleryPage;
